import type { FC } from 'react';
import type { ITracker, TrackerConfig } from './types';
import type { TAnalyticsItem } from '../types';

declare global {
  interface Window {
    klaviyo?: {
      identify: (identity: Record<string, unknown>, callback?: () => void) => void;
      track: (event: string, params: Record<string, unknown>) => void;
      push: (args: unknown[]) => void;
    };
  }
}

// Prefix ID with channel slug for multi-channel support
const prefixId = (id: string, channelSlug: string): string => {
  return `${channelSlug}__${id}`;
};

// Convert TAnalyticsItem to Klaviyo format for view events (no cart context)
const toKlaviyoViewItem = (item: TAnalyticsItem, channelSlug: string) => ({
  ProductID: prefixId(item.item_id, channelSlug),
  SKU: prefixId(item.item_id, channelSlug),
  ProductName: item.item_name,
  ItemPrice: item.price,
  ProductURL: item.item_url,
  ImageURL: item.item_image,
  ProductCategories: item.item_category ? [item.item_category] : [],
  Brand: item.item_brand,
  Currency: item.currency,
});

// Convert TAnalyticsItem to Klaviyo format for cart/purchase events
const toKlaviyoCartItem = (item: TAnalyticsItem, channelSlug: string) => ({
  ...toKlaviyoViewItem(item, channelSlug),
  Quantity: item.quantity || 1,
  RowTotal: item.price * (item.quantity || 1),
});

// Get currency from item or fall back to channel default
const getCurrency = (item: TAnalyticsItem, fallback: string) => item.currency || fallback;
const getCurrencyFromItems = (items: TAnalyticsItem[], fallback: string) =>
  items[0]?.currency || fallback;

export const createKlaviyoTracker = (companyId: string, config: TrackerConfig): ITracker => {
  const { channel, debug } = config;
  const channelSlug = channel.slug;
  const defaultCurrency = channel.defaultCurrency;

  const track = (event: string, data: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !window.klaviyo) {
      if (debug) {
        console.warn('[Klaviyo] window.klaviyo not available');
      }
      return;
    }
    const payload = { SiteId: channelSlug, ...data };
    window.klaviyo.track(event, payload);
    if (debug) {
      console.log('[Klaviyo]', event, payload);
    }
  };

  return {
    init() {
      if (typeof window === 'undefined') return;
      if (debug) {
        console.log('[Klaviyo] Initialized with', companyId, 'channel:', channelSlug);
      }
    },

    trackPageView({ url }) {
      track('Viewed Page', { URL: url });
    },

    identify({ email, data }) {
      if (typeof window === 'undefined' || !window.klaviyo) {
        if (debug) {
          console.warn('[Klaviyo] window.klaviyo not available for identify');
        }
        return;
      }
      window.klaviyo.identify({ $email: email, ...data });
      if (debug) {
        console.log('[Klaviyo] Identify', email);
      }
    },

    trackViewItem({ item }) {
      track('Viewed Product', {
        $value: item.price,
        ProductName: item.item_name,
        ProductID: prefixId(item.item_id, channelSlug),
        SKU: prefixId(item.item_id, channelSlug),
        Categories: item.item_category ? [item.item_category] : [],
        ImageURL: item.item_image,
        URL: item.item_url,
        Brand: item.item_brand,
        Price: item.price,
        Currency: getCurrency(item, defaultCurrency),
      });
    },

    trackViewItemList({ items, listId, listName }) {
      track('Viewed Category', {
        CategoryName: listName,
        CategoryID: listId,
        Currency: getCurrencyFromItems(items, defaultCurrency),
        ItemNames: items.map((item) => item.item_name),
        ProductIDs: items.map((item) => prefixId(item.item_id, channelSlug)),
        Items: items.map((item) => toKlaviyoViewItem(item, channelSlug)),
      });
    },

    trackAddToCart({ item, quantity, value }) {
      track('Added to Cart', {
        $value: value,
        Currency: getCurrency(item, defaultCurrency),
        AddedItemProductName: item.item_name,
        AddedItemProductID: prefixId(item.item_id, channelSlug),
        AddedItemSKU: prefixId(item.item_id, channelSlug),
        AddedItemCategories: item.item_category ? [item.item_category] : [],
        AddedItemImageURL: item.item_image,
        AddedItemURL: item.item_url,
        AddedItemPrice: item.price,
        AddedItemQuantity: quantity,
        ItemNames: [item.item_name],
        CheckoutURL: item.item_url,
        Items: [toKlaviyoCartItem({ ...item, quantity }, channelSlug)],
      });
    },

    trackRemoveFromCart({ item, quantity, value }) {
      track('Removed from Cart', {
        $value: value,
        Currency: getCurrency(item, defaultCurrency),
        RemovedItemProductName: item.item_name,
        RemovedItemProductID: prefixId(item.item_id, channelSlug),
        RemovedItemSKU: prefixId(item.item_id, channelSlug),
        RemovedItemPrice: item.price,
        RemovedItemQuantity: quantity,
      });
    },

    trackViewCart({ items, value }) {
      track('Viewed Cart', {
        $value: value,
        Currency: getCurrencyFromItems(items, defaultCurrency),
        ItemNames: items.map((item) => item.item_name),
        Items: items.map((item) => toKlaviyoCartItem(item, channelSlug)),
      });
    },

    trackBeginCheckout({ items, value }) {
      track('Started Checkout', {
        $value: value,
        Currency: getCurrencyFromItems(items, defaultCurrency),
        ItemNames: items.map((item) => item.item_name),
        Items: items.map((item) => toKlaviyoCartItem(item, channelSlug)),
      });
    },

    trackAddShippingInfo({ items, value, shippingTier }) {
      track('Added Shipping Info', {
        $value: value,
        Currency: getCurrencyFromItems(items, defaultCurrency),
        ShippingMethod: shippingTier,
        Items: items.map((item) => toKlaviyoCartItem(item, channelSlug)),
      });
    },

    trackAddPaymentInfo({ items, value, paymentType }) {
      track('Added Payment Info', {
        $value: value,
        Currency: getCurrencyFromItems(items, defaultCurrency),
        PaymentMethod: paymentType,
        Items: items.map((item) => toKlaviyoCartItem(item, channelSlug)),
      });
    },

    trackPurchase({ transactionId, items, value, tax, shipping }) {
      const currency = getCurrencyFromItems(items, defaultCurrency);
      track('Placed Order', {
        $event_id: transactionId,
        $value: value,
        Currency: currency,
        OrderId: transactionId,
        Categories: [...new Set(items.flatMap((item) => item.item_category ? [item.item_category] : []))],
        ItemNames: items.map((item) => item.item_name),
        Brands: [...new Set(items.flatMap((item) => item.item_brand ? [item.item_brand] : []))],
        Items: items.map((item) => toKlaviyoCartItem(item, channelSlug)),
        Tax: tax,
        Shipping: shipping,
      });

      // Also track individual ordered products
      items.forEach((item) => {
        track('Ordered Product', {
          $event_id: `${transactionId}_${item.item_id}`,
          $value: item.price * (item.quantity || 1),
          Currency: getCurrency(item, defaultCurrency),
          OrderId: transactionId,
          ProductID: prefixId(item.item_id, channelSlug),
          SKU: prefixId(item.item_id, channelSlug),
          ProductName: item.item_name,
          Quantity: item.quantity || 1,
          ProductURL: item.item_url,
          ImageURL: item.item_image,
          ProductCategories: item.item_category ? [item.item_category] : [],
          Brand: item.item_brand,
        });
      });
    },

    trackAddToWaitlist({ email, item, value }) {
      track('Added to Waitlist', {
        $value: value,
        Currency: getCurrency(item, defaultCurrency),
        Email: email,
        ProductName: item.item_name,
        ProductID: prefixId(item.item_id, channelSlug),
        SKU: prefixId(item.item_id, channelSlug),
        ProductURL: item.item_url,
        ImageURL: item.item_image,
        Price: item.price,
      });
    },

    trackEvent({ event, data }) {
      track(event, data || {});
    },
  };
};

interface KlaviyoScriptProps {
  companyId: string;
}

export const KlaviyoScript: FC<KlaviyoScriptProps> = ({ companyId }) => {
  return (
    <script
      async
      src={`//static.klaviyo.com/onsite/js/klaviyo.js?company_id=${companyId}`}
    />
  );
};
